/**
 * ConnectStepper — the step rail for the Connect-a-device wizard: checked
 * circles for done steps, the brand colour on the current one, each step's
 * icon on the rest. A done step is a button back to it (the wizard never skips
 * forward past a step, since leaving the Points step seeds the Map rows).
 * On a narrow screen only the icons show, with the current step's label.
 */

import { ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/ui/cn";
import { CONNECT_STEPS, type ConnectStepId } from "./steps";

export function ConnectStepper({
  current,
  onStep,
}: {
  current: ConnectStepId;
  /** Go back to a done step; omit for a read-only rail. */
  onStep?: (id: ConnectStepId) => void;
}) {
  const idx = CONNECT_STEPS.findIndex((s) => s.id === current);
  return (
    <nav aria-label="Connect steps">
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        {CONNECT_STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < idx;
          const active = i === idx;
          const circle = (
            <span
              className={cn(
                "flex size-6 shrink-0 items-center justify-center rounded-full transition-colors",
                done
                  ? "bg-success text-success-foreground"
                  : active
                    ? "bg-brand text-brand-foreground"
                    : "bg-surface-muted text-on-surface-muted",
              )}
            >
              {done ? <Check className="size-3" /> : <Icon className="size-3.5" />}
            </span>
          );
          const label = (
            <span
              className={cn(
                "text-xs",
                active ? "font-medium text-on-surface" : "hidden text-on-surface-muted sm:inline",
              )}
            >
              {s.label}
            </span>
          );
          return (
            <li key={s.id} className="flex items-center gap-2" aria-current={active ? "step" : undefined}>
              {done && onStep ? (
                <button
                  type="button"
                  onClick={() => onStep(s.id)}
                  title={`Back to ${s.label}: ${s.hint}`}
                  aria-label={`Back to ${s.label}`}
                  className="flex items-center gap-2 rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {circle}
                  {label}
                </button>
              ) : (
                <span className="flex items-center gap-2" title={s.hint}>
                  {circle}
                  {label}
                </span>
              )}
              {i < CONNECT_STEPS.length - 1 && (
                <ArrowRight aria-hidden className="size-3 text-on-surface-muted" />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
